import React, { useState, useContext, useRef } from "react";
import { McDrawer } from "@maersk-global/mds-react-wrapper/components-core/mc-drawer";
import { McButton } from "@maersk-global/mds-react-wrapper/components-core/mc-button";
import { McTag, McIcon } from "@maersk-global/mds-react-wrapper";
import { Box, Typography } from "@mui/material";
import { TableContext } from "../../context";
import { toast } from 'react-toastify';
import axios from "axios";

/**
 * OnboardingRequestDetailsDrawer component for viewing detailed information about onboarding requests
 * 
 * @param {boolean} isOpen - Whether the drawer is open
 * @param {Function} onClose - Function to close the drawer
 * @param {Object} requestData - The request data object containing all details
 * @param {string} requestType - Type of request ('user_request' or 'pending_approval')
 * @returns {JSX.Element} The rendered drawer component
 */
const OnboardingRequestDetailsDrawer = ({ 
  isOpen, 
  onClose, 
  requestData, 
  requestType = 'user_request',
  onApprovalComplete
}) => {
  const maerskFontFamily = '"Maersk Text", -apple-system, BlinkMacSystemFont, "Microsoft JhengHei", "Microsoft Yahei", "微软雅黑", STXihei, "华文细黑", sans-serif';

  // Context and hooks
  const { graphData } = useContext(TableContext);
  
  // State for approval/rejection functionality
  const [approvalStatus, setApprovalStatus] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [approvalData, setApprovalData] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);
  const rejectionReasonRef = useRef();

  // Helper function to format dates
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }) + ' at ' + date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'N/A';
    }
  };

  // Helper function to get status appearance
  const getStatusAppearance = (status) => {
    switch (status?.toLowerCase()) {
      case 'processing':
      case 'pending':
        return 'warning';
      case 'approved':
      case 'completed':
        return 'success';
      case 'rejected':
      case 'failed':
        return 'error';
      default:
        return 'neutral';
    }
  };

  // Helper function to get approval status display
  const getApprovalStatusDisplay = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'Pending';
      case 'approved':
        return 'Approved';
      case 'rejected':
        return 'Rejected';
      default:
        return status || 'N/A';
    }
  };

  // Helper function to get process type display
  const getProcessTypeDisplay = (changeType) => {
    switch (changeType?.toLowerCase()) {
      case 'classic':
        return 'Manual';
      case 'rapid':
        return 'Automated';
      default:
        return changeType || 'N/A';
    }
  };

  // Helper function to format approval type
  const formatApprovalType = (approvalType) => {
    return approvalType?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'N/A';
  };

  // Function to handle approval/rejection API call
  const handleApprovalSubmission = async () => {
    
    // Get the actual current value from the textarea ref
    const currentRejectionReason = rejectionReasonRef.current?.value || "";
    
    setIsProcessing(true);
    try {
      const onboardingToken = localStorage.getItem('azure_token');
      
      if (!onboardingToken) {
        throw new Error("No onboarding token available");
      }

      // Use rejection reason directly from the textarea ref
      const finalRejectionReason = approvalStatus === "rejected" ? currentRejectionReason : "";
      
      // Prepare approval notes based on status
      const approvalNotes = approvalStatus === "rejected" 
        ? finalRejectionReason.trim() 
        : `Request ${approvalStatus} by ${graphData.displayName}`;

      // Prepare the payload
      const payload = {
        onboarding_details: {
          application_id: requestData.onboarding_details?.application_id,
          application_instances: requestData.onboarding_details?.application_instances,
          owner_names: requestData.onboarding_details?.owner_names,
          change_process_type: requestData.onboarding_details?.change_process_type,
          change_assessment_summary: requestData.onboarding_details?.change_assessment_summary,
          exists_in_snow_status: requestData.onboarding_details?.exists_in_snow_status,
          approval_group: requestData.onboarding_details?.approval_group,
          assignment_group: requestData.onboarding_details?.support_group, // Using support_group as assignment_group
          cab_approval_group: "Change Management", // Default value
          requested_by: requestData.onboarding_details?.requested_by,
          request_id: requestData.request_id,
          created_date: requestData.created_date,
          updated_date: requestData.updated_date
        },
        approved_by: graphData.displayName,
        approval_type: requestData.approval_type,
        approval_status: approvalStatus,
        approval_notes: approvalNotes
      };

      const response = await axios.post(
        `${process.env.REACT_APP_ONBOARDING_URI}/change/approve_onboarding_request`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${onboardingToken}`
          }
        }
      );

      if (response.status === 200) {
        const result = response.data;
        
        if (result.status === 200) {
          const successMessage = approvalStatus === "approved" 
            ? "Approval processed successfully" 
            : "Rejection processed successfully";
          toast.success(successMessage, {
            position: "top-center",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light"
          });
          
          // Reset form and close drawer
          setApprovalStatus("");
          setApprovalData({});
          setIsFormValid(false);
          onClose();
          
          // Refresh pending approvals if callback is provided
          if (onApprovalComplete) {
            onApprovalComplete();
          }
        } else {
          throw new Error(result.error || "Approval processing failed");
        }
      } else {
        const errorData = response.data;
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      toast.error(error.message || "Failed to process approval", {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  if (!requestData) {
    return null;
  }

  const onboardingDetails = requestData.onboarding_details || {};
  const changeAssessment = onboardingDetails.change_assessment_summary || {};
  const rejectionNotes = onboardingDetails.rejection_notes || {};

  return (
    <McDrawer
      open={isOpen}
      closed={onClose}
    >
      <span slot="heading" style={{ color: "#0073ab", fontFamily: maerskFontFamily }}>
        {requestType === 'pending_approval' ? 'Approval Request Details' : 'Onboarding Request Details'}
      </span>
      
      <div style={{ fontFamily: maerskFontFamily }}>
        {/* Request Overview Section */}
        <div style={{ 
          marginBottom: "24px", 
          padding: "16px",
          borderRadius: "8px",
          border: "1px solid #e9ecef"
        }}>      
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
            <div>
              <strong style={{ color: "#555" }}>Request ID:</strong>
              <div style={{ marginTop: "4px", fontFamily: "monospace", fontSize: "14px" }}>
                {requestData.request_id || 'N/A'}
              </div>
            </div>
            <div>
              <strong style={{ color: "#555" }}>Status:</strong>
              <div style={{ marginTop: "4px" }}>
                <McTag 
                  label={requestData.request_status || 'N/A'}
                  appearance={getStatusAppearance(requestData.request_status)}
                  size="small"
                />
              </div>
            </div>
            <div>
              <strong style={{ color: "#555" }}>Created Date:</strong>
              <div style={{ marginTop: "4px" }}>
                {formatDate(requestData.created_date)}
              </div>
            </div>
            <div>
              <strong style={{ color: "#555" }}>Last Updated:</strong>
              <div style={{ marginTop: "4px" }}>
                {formatDate(requestData.updated_date)}
              </div>
            </div>
            <div>
              <strong style={{ color: "#555" }}>Requested By:</strong>
              <div style={{ marginTop: "4px" }}>
                {onboardingDetails.requested_by || 'N/A'}
              </div>
            </div>
          </div>
        </div>

        {/* Application Details Section */}
        <div style={{ 
          marginBottom: "24px", 
          padding: "16px", 
        //   backgroundColor: "#f8f9fa", 
          borderRadius: "8px",
          border: "1px solid #e9ecef"
        }}>
          <Typography variant="subtitle1" style={{ 
            marginBottom: "16px", 
            fontWeight: "500", 
            color: "#555",
            fontFamily: maerskFontFamily
          }}>
            Application Details
          </Typography>
          
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
            <div>
              <strong style={{ color: "#555" }}>Application ID:</strong>
              <div style={{ marginTop: "4px", fontFamily: "monospace", fontSize: "16px", fontWeight: "500" }}>
                {onboardingDetails.application_id || 'N/A'}
              </div>
            </div>
            <div>
              <strong style={{ color: "#555" }}>Application Instances:</strong>
              <div style={{ marginTop: "4px" }}>
                {Array.isArray(onboardingDetails.application_instances) && onboardingDetails.application_instances.length > 0 
                  ? onboardingDetails.application_instances.map((instance, index) => (
                      <div key={index} style={{ fontSize: "14px", marginBottom: index < onboardingDetails.application_instances.length - 1 ? "4px" : "0" }}>
                        {instance}
                      </div>
                    ))
                  : <div style={{ fontSize: "14px", fontStyle: "italic", color: "#888" }}>No instances</div>
                }
              </div>
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <strong style={{ color: "#555" }}>Application Owners:</strong>
              <div style={{ marginTop: "4px" }}>
                {Array.isArray(onboardingDetails.owner_names) 
                  ? onboardingDetails.owner_names.join(', ') 
                  : onboardingDetails.owner_names || 'N/A'}
              </div>
            </div>
          </div>
        </div>

        {/* Change Assessment Section */}
        {Object.keys(changeAssessment).length > 0 && (
          <div style={{ 
            marginBottom: "24px", 
            padding: "16px", 
            // backgroundColor: "#f8f9fa", 
            borderRadius: "8px",
            border: "1px solid #e9ecef"
          }}>
            <Typography variant="subtitle1" style={{ 
              marginBottom: "16px", 
              fontWeight: "500", 
              color: "#555",
              fontFamily: maerskFontFamily
            }}>
              Change Assessment Summary
            </Typography>
            
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
              {changeAssessment.deployment_method && (
                <div>
                  <strong style={{ color: "#555" }}>Deployment Method:</strong>
                  <div style={{ marginTop: "4px" }}>
                    {changeAssessment.deployment_method}
                  </div>
                </div>
              )}
              {changeAssessment.automation_maturity && (
                <div>
                  <strong style={{ color: "#555" }}>Automation Maturity:</strong>
                  <div style={{ marginTop: "4px" }}>
                    {changeAssessment.automation_maturity}
                  </div>
                </div>
              )}
              {changeAssessment.risk_history && (
                <div>
                  <strong style={{ color: "#555" }}>Risk History:</strong>
                  <div style={{ marginTop: "4px" }}>
                    {changeAssessment.risk_history}
                  </div>
                </div>
              )}
              {changeAssessment.application_type && (
                <div>
                  <strong style={{ color: "#555" }}>Application Type:</strong>
                  <div style={{ marginTop: "4px" }}>
                    {changeAssessment.application_type}
                  </div>
                </div>
              )}
              <div>
                <strong style={{ color: "#555" }}>Change Process Type:</strong>
                <div style={{ marginTop: "4px" }}>
                    <McTag 
                    label={getProcessTypeDisplay(onboardingDetails.change_process_type)}
                    appearance="info"
                    size="small"
                    />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Conditional Section: Groups & Compliance for Classic Change OR CI/CD Integration for Rapid Change */}
        {onboardingDetails.change_process_type?.toLowerCase() === 'classic' ? (
          /* Groups and Compliance Section - for Classic Change */
          <div style={{ 
            marginBottom: "24px", 
            padding: "16px", 
            borderRadius: "8px",
            border: "1px solid #e9ecef"
          }}>
            <Typography variant="subtitle1" style={{ 
              marginBottom: "16px", 
              fontWeight: "500", 
              color: "#555",
              fontFamily: maerskFontFamily
            }}>
              Groups & Compliance
            </Typography>
            
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
              <div>
                <strong style={{ color: "#555" }}>Approval Group:</strong>
                <div style={{ marginTop: "4px" }}>
                  {Array.isArray(onboardingDetails.approval_group) && onboardingDetails.approval_group.length > 0
                    ? onboardingDetails.approval_group.join(', ')
                    : onboardingDetails.approval_group || 'N/A'}
                </div>
              </div>
              <div>
                <strong style={{ color: "#555" }}>Support Group:</strong>
                <div style={{ marginTop: "4px" }}>
                  {Array.isArray(onboardingDetails.support_group) && onboardingDetails.support_group.length > 0
                    ? onboardingDetails.support_group.join(', ')
                    : onboardingDetails.support_group || 'N/A'}
                </div>
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <strong style={{ color: "#555" }}>Exists in ServiceNow:</strong>
                <div style={{ marginTop: "4px" }}>
                  <McTag 
                    label={onboardingDetails.exists_in_snow_status || 'N/A'}
                    appearance={onboardingDetails.exists_in_snow_status?.toLowerCase() === 'yes' ? 'success' : 'neutral'}
                    size="small"
                  />
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* CI/CD Integration Section - for Rapid Change */
          <div style={{ 
            marginBottom: "24px", 
            padding: "16px", 
            borderRadius: "8px",
            border: "1px solid #e9ecef"
          }}>
            <Typography variant="subtitle1" style={{ 
              marginBottom: "16px", 
              fontWeight: "500", 
              color: "#555",
              fontFamily: maerskFontFamily
            }}>
              CI/CD Integration
            </Typography>
            
            <div style={{ display: "flex", flexDirection: "column" }}>
              <div style={{ 
                display: "flex", 
                alignItems: "flex-start", 
                gap: "12px",
                padding: "12px",
                paddingLeft: 0
              }}>
                <McIcon 
                  icon="check-circle" 
                  size="20" 
                  color="#28a745"
                />
                <div style={{ color: "#555" }}>GitHub repository mapping completed</div>
              </div>
              <div style={{ 
                display: "flex", 
                alignItems: "flex-start", 
                gap: "12px",
                padding: "12px",
                paddingLeft: 0
              }}>
                <McIcon 
                  icon="check-circle" 
                  size="20" 
                  color="#28a745"
                />
                <div style={{ color: "#555" }}>Application Certification Completed in MSA</div>
              </div>
            </div>
          </div>
        )}

        {/* Approval Status Section - Only for user requests */}
        {requestType === 'user_request' && (
          <div style={{ 
            marginBottom: "24px", 
            padding: "16px", 
            // backgroundColor: "#f8f9fa", 
            borderRadius: "8px",
            border: "1px solid #e9ecef"
          }}>
            <Typography variant="subtitle1" style={{ 
              marginBottom: "16px", 
              fontWeight: "500", 
              color: "#555",
              fontFamily: maerskFontFamily
            }}>
              Approval Status
            </Typography>
            
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
              <div>
                <strong style={{ color: "#555" }}>Engineering Team Approval:</strong>
                <div style={{ marginTop: "4px" }}>
                  <McTag 
                    label={getApprovalStatusDisplay(onboardingDetails.engineering_team_approval_status)}
                    appearance={getStatusAppearance(onboardingDetails.engineering_team_approval_status)}
                    size="small"
                  />
                </div>
              </div>
              <div>
                <strong style={{ color: "#555" }}>Change Manager Approval:</strong>
                <div style={{ marginTop: "4px" }}>
                  <McTag 
                    label={getApprovalStatusDisplay(onboardingDetails.change_manager_approval_status)}
                    appearance={getStatusAppearance(onboardingDetails.change_manager_approval_status)}
                    size="small"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Rejection Notes Section - Only show if there are rejection notes */}
        {requestType === 'user_request' && Object.keys(rejectionNotes).length > 0 && (
          <div style={{ 
            marginBottom: "24px", 
            padding: "16px", 
            backgroundColor: "#fff3cd", 
            borderRadius: "8px",
            border: "1px solid #ffeaa7"
          }}>
            <Typography variant="subtitle1" style={{ 
              marginBottom: "16px", 
              fontWeight: "500", 
              color: "#856404",
              fontFamily: maerskFontFamily
            }}>
              Rejection Notes
            </Typography>
            
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {Object.entries(rejectionNotes).map(([key, value]) => (
                <div key={key} style={{ 
                  padding: "12px", 
                  backgroundColor: "#ffffff", 
                  borderRadius: "6px",
                  border: "1px solid #e9ecef"
                }}>
                  <strong style={{ color: "#856404", textTransform: "capitalize" }}>
                    {key.replace('_', ' ')}:
                  </strong>
                  <div style={{ marginTop: "4px", color: "#333" }}>
                    {value || 'No notes provided'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Approve/Reject Section - Only for pending approval requests */}
        {requestType === 'pending_approval' && (
          <div style={{ 
            marginBottom: "24px", 
            padding: "16px", 
            backgroundColor: "#f8f9fa", 
            borderRadius: "8px",
            border: "1px solid #e9ecef"
          }}>
            <Typography variant="subtitle1" style={{ 
              marginBottom: "16px", 
              fontWeight: "500", 
              color: "#555",
              fontFamily: maerskFontFamily
            }}>
              Approve/Reject Request
            </Typography>
            
            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              {/* Approval Type Display */}
              <div style={{ display: "flex", flexDirection: "row", gap: "8px", alignItems: "center", justifyContent: "space-between" }}>
                <strong style={{ color: "#555" }}>Approval Type:</strong>
                <McTag 
                  label={formatApprovalType(requestData.approval_type)}
                  appearance="info"
                  size="small"
                />
              </div>

              {/* Approval Status Selection */}
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                <strong style={{ color: "#555" }}>
                  Approval Status <span style={{ color: "red" }}>*</span>:
                </strong>
                <select
                  value={approvalStatus}
                  onChange={(e) => {
                    const value = e.target.value;
                    setApprovalStatus(value);
                    if (value !== "rejected") {
                      setApprovalData((prev) => ({
                        ...prev,
                        approval_status: value,
                        rejection_reason: ""
                      }));
                      setIsFormValid(true); // Approved is always valid
                    } else {
                      setApprovalData((prev) => ({
                        ...prev,
                        approval_status: value,
                        rejection_reason: prev.rejection_reason || ""
                      }));
                      // For rejected, check if we already have a rejection reason
                      setIsFormValid(false); // Will be set to true when user types rejection reason
                    }
                  }}
                  style={{
                    padding: "8px 12px",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                    outline: "none",
                    fontSize: "14px",
                    backgroundColor: "#fff"
                  }}
                >
                  <option value="" disabled>Select approval status</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>

              {/* Rejection Reason - only show when status is rejected */}
              {approvalStatus === "rejected" && (
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  <strong style={{ color: "#555" }}>
                    Reason for Rejection <span style={{ color: "red" }}>*</span>:
                  </strong>
                  <textarea
                    ref={rejectionReasonRef}
                    value={approvalData.rejection_reason || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      setApprovalData((prev) => ({
                        ...prev,
                        rejection_reason: value
                      }));
                      // Update form validity based on whether we have any text
                      setIsFormValid(value.trim().length > 0);
                    }}
                    placeholder="Please provide a detailed reason for rejection..."
                    style={{
                      padding: "8px 12px",
                      borderRadius: "4px",
                      border: "1px solid #ccc",
                      minHeight: "80px",
                      resize: "vertical",
                      outline: "none",
                      fontSize: "14px",
                      fontFamily: maerskFontFamily
                    }}
                  />
                </div>
              )}

              {/* Action Buttons */}
              {approvalStatus && (
                <div style={{ 
                  display: "flex", 
                  gap: "12px",
                  paddingTop: "10px",
                }}>
                  <McButton 
                    variant="filled"
                    appearance="primary"
                    disabled={
                      isProcessing || 
                      !approvalStatus || 
                      (approvalStatus === "rejected" && !isFormValid)
                    }
                    loading={isProcessing}
                    onClick={handleApprovalSubmission}
                  >
                    {approvalStatus === "approved" ? "Approve Request" : "Reject Request"}
                  </McButton>
                  
                  <McButton 
                    variant="filled"
                    appearance="neutral"
                    disabled={isProcessing}
                    onClick={() => {
                      setApprovalStatus("");
                      setApprovalData({});
                      setIsFormValid(false);
                    }}
                  >
                    Reset
                  </McButton>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div slot="footer" style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
        <McButton 
          variant="filled" 
          appearance="primary"
          onClick={onClose}
        >
          Close
        </McButton>
      </div>
    </McDrawer>
  );
};

export default OnboardingRequestDetailsDrawer;
